/**
 * Vault Document Scanner Widget (TelePortal Setup R1)
 *
 * Self-fetches documents from the user's Vault via the SDK hook — every
 * document they can link, whichever hero keeps it (`vaultPickableFiles`, the
 * rule every Vault pick follows, D1: in storage, not rejected, the newest
 * first) whose type is known (D2) — the engine matches a file to a task by
 * its type, so a file of no type could only ever be "linked" to nothing.
 * Users can link documents to their relocation plan or skip them.
 * Linked documents are passed to the instantiation engine for task matching.
 * Auto-skips (completes with empty payload) when no documents are found.
 */

import type { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation, useLocale } from '@tonnie/locale';
import { Button, Card, CardContent, Badge, cn } from '@tonnie/ui';
import {
  FileText,
  Link2,
  SkipForward,
  Check,
  Upload,
  Calendar,
  Clock,
  AlertTriangle,
  Loader2,
} from '@tonnie/assets';
import { motion, AnimatePresence } from 'framer-motion';
import type { WidgetProps } from '@tonnie/copilot';
import { documentTypeLabelKey, vaultPickableFiles } from '@tonnie/heroes';
import { useVaultFiles, type VaultFile } from '@tonnie/sdk';
import { TonnieCheckedBadge } from '@/components/shared/TonnieCheckedBadge';
import { getCertificationLabel } from '@/shared/utils/document-helpers';

interface VaultDocument {
  id: string;
  name: string;
  document_type: string;
  file_type: string;
  uploaded_at: string;
  size_bytes?: number;
  // BL-100: metadata the Vault DTO already carries — surfaced so the user can
  // identify + confidently match each file to its relocation task.
  status?: string;
  tonnie_checked?: boolean;
  expires_at?: string;
  teleportal_track?: string;
  requires_certification?: boolean;
  certification_type?: string;
}

type LinkStatus = 'pending' | 'linked' | 'skipped';

/** The Vault serves up to this many files a page — the person's whole Vault, in practice. */
const VAULT_PAGE = 500;

function mapVaultFilesToDocuments(files: VaultFile[]): VaultDocument[] {
  return files.flatMap((f) => {
    if (!f.documentType) return [];
    return [
      {
        id: f.id,
        name: f.fileName,
        document_type: f.documentType,
        file_type: f.fileType,
        uploaded_at: f.uploadedAt,
        size_bytes: f.fileSize,
        status: f.status,
        tonnie_checked: f.tonnieChecked,
        expires_at: f.expiresAt,
        teleportal_track: typeof f.teleportalTrack === 'string' ? f.teleportalTrack : undefined,
        requires_certification: f.documentMetadata?.requiresCertification,
        certification_type: f.documentMetadata?.certificationType,
      },
    ];
  });
}

/** Expiry state for an optional ISO date, relative to `now`. */
function expiryState(expiresAt?: string): 'expired' | 'expiring' | null {
  if (!expiresAt) return null;
  const ts = new Date(expiresAt).getTime();
  if (Number.isNaN(ts)) return null;
  const now = Date.now();
  if (ts < now) return 'expired';
  if (ts - now <= 30 * 24 * 60 * 60 * 1000) return 'expiring';
  return null;
}

export function VaultDocumentScanner({ onComplete, config: _config }: WidgetProps) {
  const { t } = useTranslation();

  const { data: vaultResponse, isLoading: isFetchingVault } = useVaultFiles({
    limit: VAULT_PAGE,
  });

  const vaultDocuments: VaultDocument[] = useMemo(() => {
    if (!vaultResponse?.files) return [];
    return mapVaultFilesToDocuments(vaultPickableFiles(vaultResponse.files));
  }, [vaultResponse]);

  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (isFetchingVault) return undefined;
    if (vaultDocuments.length === 0) {
      const timer = setTimeout(() => {
        onCompleteRef.current?.({ linked_documents: [] });
      }, 1500);
      return () => {
        clearTimeout(timer);
      };
    }
    return undefined;
  }, [isFetchingVault, vaultDocuments]);

  const [statuses, setStatuses] = useState<Record<string, LinkStatus>>({});

  const linkedDocs = useMemo(
    () => vaultDocuments.filter((d) => statuses[d.id] === 'linked'),
    [vaultDocuments, statuses]
  );

  const pendingCount = useMemo(
    () => vaultDocuments.filter((d) => !statuses[d.id] || statuses[d.id] === 'pending').length,
    [vaultDocuments, statuses]
  );

  const handleLink = useCallback((docId: string) => {
    setStatuses((prev) => ({ ...prev, [docId]: 'linked' }));
  }, []);

  const handleSkip = useCallback((docId: string) => {
    setStatuses((prev) => ({ ...prev, [docId]: 'skipped' }));
  }, []);

  const handleUndo = useCallback((docId: string) => {
    setStatuses((prev) => ({ ...prev, [docId]: 'pending' }));
  }, []);

  const handleComplete = useCallback(() => {
    const linked_documents = linkedDocs.map((doc) => ({
      document_type: doc.document_type,
      vault_document_id: doc.id,
      document_name: doc.name,
    }));
    onComplete?.({ linked_documents });
  }, [linkedDocs, onComplete]);

  if (isFetchingVault) {
    return (
      <Card
        className="border-border/50"
        data-testid="widget-vault_document_scanner"
        data-state="loading"
      >
        <CardContent className="py-8 text-center">
          <Loader2 className="w-8 h-8 mx-auto text-primary-accessible animate-spin mb-3" />
          <p className="text-sm text-muted-foreground">{t('teleportal.vault.scanning')}</p>
        </CardContent>
      </Card>
    );
  }

  if (vaultDocuments.length === 0) {
    return (
      <Card
        className="border-border/50"
        data-testid="widget-vault_document_scanner"
        data-state="empty"
      >
        <CardContent className="py-8 text-center">
          <Upload className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
          <h4 className="font-semibold text-sm mb-1">{t('teleportal.vault.empty')}</h4>
          <p className="text-xs text-muted-foreground max-w-sm mx-auto">
            {t('teleportal.vault.emptyMessage')}
          </p>
          <Loader2 className="w-4 h-4 mx-auto text-muted-foreground animate-spin mt-3" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="widget-vault_document_scanner" data-state="ready">
      {/* Summary */}
      <div className="flex items-center justify-between">
        <div className="text-sm">
          <span className="font-medium">{vaultDocuments.length}</span>{' '}
          <span className="text-muted-foreground">{t('teleportal.vault.documentsFound')}</span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          {linkedDocs.length > 0 && (
            <Badge variant="outline" className="text-tone-green border-tone-green-line">
              <Link2 className="w-3 h-3 mr-1" />
              {linkedDocs.length} {t('teleportal.vault.linked')}
            </Badge>
          )}
          {pendingCount > 0 && (
            <Badge variant="outline" className="text-muted-foreground">
              {pendingCount} {t('teleportal.vault.remaining')}
            </Badge>
          )}
        </div>
      </div>

      {/* Document Cards */}
      <div className="space-y-2">
        <AnimatePresence>
          {vaultDocuments.map((doc) => {
            const status = statuses[doc.id] ?? 'pending';
            return (
              <DocumentCard
                key={doc.id}
                doc={doc}
                status={status}
                onLink={() => handleLink(doc.id)}
                onSkip={() => handleSkip(doc.id)}
                onUndo={() => handleUndo(doc.id)}
                t={t}
              />
            );
          })}
        </AnimatePresence>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-2 border-t border-border">
        <p className="text-xs text-muted-foreground">{t('teleportal.vault.hint')}</p>
        <Button
          size="sm"
          onClick={handleComplete}
          data-testid="widget-vault_document_scanner-complete"
        >
          {pendingCount > 0
            ? t('teleportal.vault.continueWithPending', {
                count: pendingCount,
              })
            : t('teleportal.vault.continue')}
        </Button>
      </div>
    </div>
  );
}

function DocumentCard({
  doc,
  status,
  onLink,
  onSkip,
  onUndo,
  t,
}: {
  doc: VaultDocument;
  status: LinkStatus;
  onLink: () => void;
  onSkip: () => void;
  onUndo: () => void;
  t: TFunction;
}) {
  // Dates and sizes in the APP locale (F34).
  const { formatDate, formatFileSize } = useLocale();
  const labelKey = documentTypeLabelKey(doc.document_type);
  const typeLabel = labelKey ? t(labelKey) : doc.name;
  const expiry = expiryState(doc.expires_at);
  const hasMeta =
    doc.tonnie_checked ||
    !!expiry ||
    doc.requires_certification ||
    !!doc.teleportal_track ||
    doc.status === 'verified';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        'flex items-center gap-3 p-3 rounded-lg border transition-colors',
        status === 'linked' && 'border-tone-green-line bg-tone-green-subtle',
        status === 'skipped' && 'border-border/30 bg-muted/30',
        status === 'pending' && 'border-border hover:bg-muted/20'
      )}
    >
      <div
        className={cn(
          'w-10 h-10 rounded-lg flex items-center justify-center shrink-0',
          status === 'linked' ? 'bg-tone-green-subtle' : 'bg-muted/50'
        )}
      >
        {status === 'linked' ? (
          <Check className="w-5 h-5 text-tone-green" />
        ) : (
          <FileText className="w-5 h-5 text-muted-foreground" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{doc.name}</div>
        <div className="flex items-center gap-2 text-2xs text-muted-foreground mt-0.5">
          <Badge variant="secondary" className="text-2xs py-0 px-1.5">
            {typeLabel}
          </Badge>
          <span className="flex items-center gap-0.5">
            <Calendar className="w-2.5 h-2.5" />
            {formatDate(doc.uploaded_at)}
          </span>
          {doc.size_bytes && <span>{formatFileSize(doc.size_bytes)}</span>}
        </div>
        {/* BL-100: metadata pills so the user can identify + match the file. */}
        {hasMeta && (
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            {doc.tonnie_checked && <TonnieCheckedBadge size="sm" />}
            {expiry === 'expired' && (
              <Badge
                variant="outline"
                className="text-2xs py-0 px-1.5 gap-0.5 border-tone-red-line text-tone-red"
              >
                <AlertTriangle className="w-2.5 h-2.5" />
                {t('teleportal.vault.expired')}
              </Badge>
            )}
            {expiry === 'expiring' && (
              <Badge
                variant="outline"
                className="text-2xs py-0 px-1.5 gap-0.5 border-tone-amber-line text-tone-amber"
              >
                <Clock className="w-2.5 h-2.5" />
                {t('teleportal.vault.expiringSoon')}
              </Badge>
            )}
            {!expiry && doc.expires_at && (
              <Badge
                variant="outline"
                className="text-2xs py-0 px-1.5 gap-0.5 text-muted-foreground"
              >
                <Clock className="w-2.5 h-2.5" />
                {t('teleportal.vault.validUntil', {
                  date: formatDate(doc.expires_at),
                })}
              </Badge>
            )}
            {doc.requires_certification && (
              <Badge
                variant="outline"
                className="text-2xs py-0 px-1.5 border-tone-amber-line text-tone-amber bg-tone-amber-subtle"
              >
                {getCertificationLabel(doc.certification_type, t)}
              </Badge>
            )}
            {doc.teleportal_track && (
              <Badge variant="outline" className="text-2xs py-0 px-1.5 text-muted-foreground">
                {t(`teleportal.tracks.${doc.teleportal_track}.name`)}
              </Badge>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-1.5 shrink-0">
        {status === 'pending' ? (
          <>
            <Button
              variant="default"
              size="sm"
              className="h-7 text-xs bg-tone-green-solid text-tone-green-on-solid hover:bg-tone-green-solid-hover"
              onClick={onLink}
              data-testid="widget-vault_document_scanner-link-doc"
            >
              <Link2 className="w-3 h-3 mr-1" />
              {t('teleportal.vault.link')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={onSkip}
              data-testid="widget-vault_document_scanner-skip"
            >
              <SkipForward className="w-3 h-3 mr-1" />
              {t('teleportal.vault.skip')}
            </Button>
          </>
        ) : (
          <>
            {/* A skipped row says so in words — it is not faded (axe, block 77). */}
            {status === 'skipped' && (
              <span className="text-2xs text-muted-foreground">
                {t('teleportal.vault.skipped')}
              </span>
            )}
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onUndo}>
              {t('teleportal.vault.undo')}
            </Button>
          </>
        )}
      </div>
    </motion.div>
  );
}
